import React, { useCallback, useEffect, useState } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import { FormLockedType } from '../../../../types/FormLockedType'
import { useStyles } from './FlyOutNotificationSection.styles'
import {
  lifeCycleNotificationsTextAreaMappings,
  listingNotificationTextAreaMappings,
  meteringNotificationTextAreaMappings,
  privateOfferNotificationTeactAreaMappings,
  resaleAuthorizationTextareaMappings,
} from '../../../../common/utils/productCreationUtil'

import { FlyOutNotificationsSettings } from '../../../AddEditProductFormComponents/FlyOutNotificationsSettings/FlyOutNotificationsSettings'
import { defaultNow } from '../../../../common/utils/dateTimeNow'
import {
  FlyoutProductAddEditSectionName,
  FlyoutProductSections,
  NotificationRecipients,
} from '../../../../types/addEditProduct'
import {
  areAllEmailsValid,
  getUpdatedTags,
} from '../../../../common/utils/helperFunctions'
import { isEmpty } from 'lodash'
import {
  allFieldsArrayNotifications,
  awsRequirementsLink,
} from '../../../../common/utils/constants'

import { faBell, faLockA, faMeter } from '@fortawesome/pro-regular-svg-icons'
import { NotificationEmailChipInput } from '../../../AddEditProductFormComponents/NotificationEmailChipInput/NotificationEmailChipInput'
import { NotificationBoxWrapper } from './NotificationBoxWrapper'
import { FlyOutProductAdhere } from '../../../AddEditProductFormComponents/FlyOutProductAdhere/FlyOutProductAdhere'
import { PlanType } from '@labrav/react-components'

export interface FlyOutNotificationSectionProps {
  detailView?: boolean
  detailViewTooltip?: string
  productView?: boolean
}

export const FlyOutNotificationSection = ({
  isFormLocked,
  detailView,
  detailViewTooltip,
  productView,
}: FlyOutNotificationSectionProps & FormLockedType) => {
  const { classes } = useStyles()

  const { values, errors, touched, handleChange, setFieldValue, handleBlur } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const notificationRecipients = values.notificationRecipients
  const notificationRecipientsErrors = errors.notificationRecipients
  const notificationRecipientsTouched = touched.notificationRecipients
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const productType = values.productDetails.productType
  const isProfessionalService = productType === PlanType.PROFESSIONAL_SERVICES
  const requirementsLink = isProfessionalService ? '' : awsRequirementsLink

  useEffect(() => {
    if (notificationRecipients?.privateOfferCreated) {
      const newValues = [...new Set(notificationRecipients.privateOfferCreated)]
      //all these four feld will have same value so using only awsCppoAcceptedSeller for all
      const existingValues =
        (notificationRecipients as any)?.awsCppoAcceptedSeller || []
      const mergedValues = [...new Set([...existingValues, ...newValues])]
      const allNotificationRecipients = { ...values.notificationRecipients }

      const updatedNotifications = {
        ...allNotificationRecipients,
        awsCppoAcceptedSeller: mergedValues,
        awsCppoCreationFailed: mergedValues,
        awsCppoCreationSuccess: mergedValues,
        awsRegistrationIncompleteCppo: mergedValues,
      }
      setFieldValue('notificationRecipients', updatedNotifications)
    }
  }, [])

  const updateOtherField = useCallback(
    async (key: string, updatedTags: string[]) => {
      const updatedTagsValue = [...updatedTags]
      const allNotificationRecipients = { ...values.notificationRecipients }
      switch (key) {
        case 'privateOfferCreated': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            privateOfferCreated: updatedTagsValue,
            awsPrivateOfferCreationFailed: updatedTagsValue,
            awsPrivateOfferCreationSuccess: updatedTagsValue,
            awsCppoAcceptedSeller: updatedTagsValue,
            awsCppoCreationFailed: updatedTagsValue,
            awsCppoCreationSuccess: updatedTagsValue,
            awsRegistrationIncompleteCppo: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }
        case 'awsPrivateOfferRestrictionSuccess': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            awsPrivateOfferRestrictionSuccess: updatedTagsValue,
            awsPrivateOfferRestrictionFailed: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }

        case 'freeTrialActivated': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            freeTrialActivated: updatedTagsValue,
            freeTrialEnabled: updatedTagsValue,
            freeTrialActivationInProgress: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }

        case 'freeTrialDeactivated': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            freeTrialDeactivated: updatedTagsValue,
            freeTrialDeactivationInProgress: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }

        case 'awsResellerAgreementCreated': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            awsResellerAgreementCreated: updatedTagsValue,
            awsResellerAgreementCreationSuccess: updatedTagsValue,
            awsResellerAgreementCreationFailed: updatedTagsValue,
            awsResellerAgreementActiveSeller: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }

        case 'awsResellerAgreementDeactivated': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            awsResellerAgreementDeactivated: updatedTagsValue,
            freeTrialDeactivationInProgress: updatedTagsValue,
            awsResellerAgreementDeactivatedSeller: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }

        case 'registrationCompleted': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            registrationCompleted: updatedTagsValue,
            awsFdaStartedSeller: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }

        case 'registrationIncompleteOffer': {
          const updatedNotifications = {
            ...allNotificationRecipients,
            registrationIncompleteSubscription: updatedTagsValue,
            registrationIncompleteContract: updatedTagsValue,
            registrationIncompleteOffer: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
          break
        }
        default: {
          const updatedNotifications = {
            ...allNotificationRecipients,
            [key]: updatedTagsValue,
          }
          await setFieldValue('notificationRecipients', updatedNotifications)
        }
      }
    },
    [values.notificationRecipients]
  )

  const handleTextAreaChange = (newTags: string, key: string) => {
    const notification =
      notificationRecipients?.[key as keyof NotificationRecipients] || []

    const updatedTags = getUpdatedTags(notification, newTags)
    updateOtherField(key, updatedTags)
  }

  const handleDelete = async (value: string, key: string) => {
    const filteredOptions = [
      ...notificationRecipients?.[key as keyof NotificationRecipients].filter(
        option => option !== value
      ),
    ]
    await updateOtherField(key, filteredOptions)
  }

  const getIfError = (key: string) => {
    return (
      !!notificationRecipientsTouched?.[key as keyof NotificationRecipients] &&
      !!notificationRecipientsErrors?.[key as keyof NotificationRecipients]
    )
  }
  const getErrorText = (key: string) => {
    const error =
      (!!notificationRecipientsTouched?.[key as keyof NotificationRecipients] &&
        notificationRecipientsErrors?.[key as keyof NotificationRecipients]) ||
      ''
    if (error instanceof Array) {
      const newErrors = new Set(error)
      const filteredError = [...newErrors]
      return filteredError.join('')
    } else {
      return error
    }
  }

  const disabledConfigure =
    !focusedField ||
    getIfError(focusedField) ||
    isEmpty(
      notificationRecipients[focusedField as keyof NotificationRecipients]
    ) ||
    !areAllEmailsValid(
      notificationRecipients[focusedField as keyof NotificationRecipients]
    )

  const configureEmailsInAllInputs = async () => {
    const updatedFields: { [key: string]: string[] } = {}
    const emailsToAdd =
      notificationRecipients[focusedField as keyof NotificationRecipients]

    allFieldsArrayNotifications.forEach(key => {
      if (
        !isEmpty(
          notificationRecipients[focusedField as keyof NotificationRecipients]
        )
      ) {
        const updatedValue = [...emailsToAdd]
        updatedFields[`${key}`] = updatedValue
      }
    })
    await handleChange({
      target: {
        name: 'notificationRecipients',
        value: updatedFields,
      },
    })
  }

  return (
    <>
      <div className={classes.container}>
        <FlyOutNotificationsSettings
          isFormLocked={isFormLocked}
          now={defaultNow}
          detailView={detailView}
          detailViewTooltip={detailViewTooltip}
        />
        <NotificationBoxWrapper
          heading={'Buyer lifecycle notifications'}
          helperSectionName="flyOutBuyerLifecycleNotifications"
          icon={faBell}
        >
          <NotificationEmailChipInput
            handleTextAreaChange={handleTextAreaChange}
            handleDelete={handleDelete}
            getIfError={getIfError}
            getErrorText={getErrorText}
            isFormLocked={isFormLocked}
            configureEmailsInAllInputs={configureEmailsInAllInputs}
            disabledConfigure={disabledConfigure}
            focusedField={focusedField}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
            setFocusedField={setFocusedField}
            handleBlur={handleBlur}
            textAreaMappings={lifeCycleNotificationsTextAreaMappings}
            notificationRecipients={notificationRecipients}
            productView={productView}
          />
        </NotificationBoxWrapper>
        <NotificationBoxWrapper
          heading={'Listing notifications'}
          helperSectionName="flyOutListingNotifications"
          icon={faBell}
        >
          <NotificationEmailChipInput
            handleTextAreaChange={handleTextAreaChange}
            handleDelete={handleDelete}
            getIfError={getIfError}
            getErrorText={getErrorText}
            isFormLocked={isFormLocked}
            configureEmailsInAllInputs={configureEmailsInAllInputs}
            disabledConfigure={disabledConfigure}
            focusedField={focusedField}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
            setFocusedField={setFocusedField}
            handleBlur={handleBlur}
            textAreaMappings={listingNotificationTextAreaMappings}
            notificationRecipients={notificationRecipients}
            productView={productView}
          />
        </NotificationBoxWrapper>
        <NotificationBoxWrapper
          heading={'Private offer notifications'}
          helperSectionName="flyOutPrivateOfferNotifications"
          icon={faLockA}
        >
          <NotificationEmailChipInput
            handleTextAreaChange={handleTextAreaChange}
            handleDelete={handleDelete}
            getIfError={getIfError}
            getErrorText={getErrorText}
            isFormLocked={isFormLocked}
            configureEmailsInAllInputs={configureEmailsInAllInputs}
            disabledConfigure={disabledConfigure}
            focusedField={focusedField}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
            setFocusedField={setFocusedField}
            handleBlur={handleBlur}
            textAreaMappings={privateOfferNotificationTeactAreaMappings}
            notificationRecipients={notificationRecipients}
            productView={productView}
          />
        </NotificationBoxWrapper>

        <NotificationBoxWrapper
          heading={'Resale authorization notifications'}
          helperSectionName="flyOutResaleNotifications"
          icon={faMeter}
        >
          <NotificationEmailChipInput
            handleTextAreaChange={handleTextAreaChange}
            handleDelete={handleDelete}
            getIfError={getIfError}
            getErrorText={getErrorText}
            isFormLocked={isFormLocked}
            configureEmailsInAllInputs={configureEmailsInAllInputs}
            disabledConfigure={disabledConfigure}
            focusedField={focusedField}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
            setFocusedField={setFocusedField}
            handleBlur={handleBlur}
            textAreaMappings={resaleAuthorizationTextareaMappings}
            notificationRecipients={notificationRecipients}
            productView={productView}
          />
        </NotificationBoxWrapper>
        <NotificationBoxWrapper
          heading={'Metering notifications'}
          helperSectionName="flyOutMeteringNotifications"
          icon={faMeter}
        >
          <NotificationEmailChipInput
            handleTextAreaChange={handleTextAreaChange}
            handleDelete={handleDelete}
            getIfError={getIfError}
            getErrorText={getErrorText}
            isFormLocked={isFormLocked}
            configureEmailsInAllInputs={configureEmailsInAllInputs}
            disabledConfigure={disabledConfigure}
            focusedField={focusedField}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
            setFocusedField={setFocusedField}
            handleBlur={handleBlur}
            textAreaMappings={meteringNotificationTextAreaMappings}
            notificationRecipients={notificationRecipients}
            productView={productView}
          />
        </NotificationBoxWrapper>
        {!productView && (
          <FlyOutProductAdhere
            requirementsLink={requirementsLink}
            sectionName={
              FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS
            }
            isFormLocked={isFormLocked}
          />
        )}
      </div>
    </>
  )
}
